import {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
	useRef,
	type ReactNode,
} from "react"
import { useAuth } from "../providers/auth"
import { useWebRTCConnection } from "../hooks/cursor/useWebRTCConnection"
import { useCursorInterpolation } from "../hooks/cursor/useCursorInterpolation"
import type {
	CursorContextValue,
	CursorState,
	CursorUserInfo,
	InterpolatedCursor,
	ConnectionState,
} from "../hooks/cursor/types"

const CursorContext = createContext<CursorContextValue | undefined>(undefined)

export function CursorProvider({ children }: { children: ReactNode }) {
	const { user } = useAuth()

	const [connectionState, setConnectionState] =
		useState<ConnectionState>("disconnected")
	const [connectionId, setConnectionId] = useState<string | null>(null)
	const [latency, setLatency] = useState<number | null>(null)
	const [localCursor, setLocalCursor] = useState<CursorState | null>(null)
	const [remoteCursors, setRemoteCursors] = useState<
		Map<string, InterpolatedCursor>
	>(new Map())
	const [peers, setPeers] = useState<Map<string, CursorUserInfo>>(new Map())
	const [debugMode, setDebugMode] = useState(false)

	const cursorsRef = useRef<Map<string, InterpolatedCursor>>(new Map())
	const projectIdRef = useRef<string | null>(null)
	// Store the server-assigned connectionId for this tab
	const connectionIdRef = useRef<string | null>(null)

	// Initialize interpolation first (needed by handlers below)
	const { updateCursor, removeCursor } = useCursorInterpolation(
		cursorsRef,
		setRemoteCursors,
	)

	const handleCursorUpdate = useCallback(
		(cursor: CursorState) => {
			// Don't process our own cursor (use connectionId for identification)
			if (cursor.connectionId === connectionIdRef.current) return
			updateCursor(cursor)
		},
		[updateCursor],
	)

	const handlePeerJoined = useCallback((peer: CursorUserInfo) => {
		// Use connectionId as the key
		setPeers((prev) => new Map(prev).set(peer.connectionId, peer))
	}, [])

	const handlePeerLeft = useCallback(
		(connectionId: string) => {
			setPeers((prev) => {
				const next = new Map(prev)
				next.delete(connectionId)
				return next
			})
			removeCursor(connectionId)
		},
		[removeCursor],
	)

	const handleRoomJoined = useCallback(
		(newConnectionId: string, _userInfo: CursorUserInfo) => {
			// Store our server-assigned connectionId
			connectionIdRef.current = newConnectionId
			setConnectionId(newConnectionId)

			// Clear stale peers and cursors when (re)joining a room
			// This handles both initial connection and reconnection after server restart
			setPeers(new Map())
			setRemoteCursors(new Map())
			cursorsRef.current.clear()
		},
		[],
	)

	const {
		connect: rtcConnect,
		disconnect: rtcDisconnect,
		sendCursor,
	} = useWebRTCConnection({
		projectId: projectIdRef.current,
		onCursorUpdate: handleCursorUpdate,
		onPeerJoined: handlePeerJoined,
		onPeerLeft: handlePeerLeft,
		onRoomJoined: handleRoomJoined,
		onConnectionStateChange: setConnectionState,
		onLatencyUpdate: setLatency,
	})

	const connect = useCallback(
		(projectId: string) => {
			projectIdRef.current = projectId
			// connectionId will be assigned by server in room-joined message
			connectionIdRef.current = null

			// Clear stale peers and cursors before connecting/reconnecting
			setPeers(new Map())
			setRemoteCursors(new Map())
			cursorsRef.current.clear()

			// Session cookie is sent automatically by the browser
			rtcConnect()
		},
		[rtcConnect],
	)

	const disconnect = useCallback(() => {
		rtcDisconnect()
		projectIdRef.current = null
		connectionIdRef.current = null
		setConnectionId(null)
		setPeers(new Map())
		setRemoteCursors(new Map())
		cursorsRef.current.clear()
	}, [rtcDisconnect])

	const updateLocalCursor = useCallback(
		(update: Partial<CursorState>) => {
			const projectId = projectIdRef.current
			const connectionId = connectionIdRef.current
			// Don't send cursor updates until we have a connectionId from the server
			if (!user?.id || !projectId || !connectionId) return

			setLocalCursor((prev: CursorState | null) => {
				const next: CursorState = {
					connectionId,
					userId: user.id,
					projectId,
					position: update.position || prev?.position || { x: 0, y: 0 },
					viewport: update.viewport ||
						prev?.viewport || { x: 0, y: 0, zoom: 1 },
					state: update.state || prev?.state || "idle",
					draggedNodes: update.draggedNodes,
					connectingFrom: update.connectingFrom,
					selectionBox: update.selectionBox,
					timestamp: Date.now(),
					sequenceNumber: (prev?.sequenceNumber || 0) + 1,
				}

				// Send to server
				sendCursor(next)

				return next
			})
		},
		[user?.id, sendCursor],
	)

	const value: CursorContextValue = useMemo(
		() => ({
			connectionState,
			connectionId,
			latency,
			localCursor,
			updateCursor: updateLocalCursor,
			remoteCursors,
			peers,
			connect,
			disconnect,
			debugMode,
			setDebugMode,
		}),
		[
			connectionState,
			connectionId,
			latency,
			localCursor,
			updateLocalCursor,
			remoteCursors,
			peers,
			connect,
			disconnect,
			debugMode,
		],
	)

	return (
		<CursorContext.Provider value={value}>{children}</CursorContext.Provider>
	)
}

export function useCursor() {
	const context = useContext(CursorContext)
	if (!context) {
		throw new Error("useCursor must be used within a CursorProvider")
	}
	return context
}
